import * as React from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { Clock, WifiOff } from "lucide-react";
import { TOTP_CODE_LENGTH } from "@taskiq/shared";
import { AuthLayout } from "@/components/AuthLayout";
import { CodeInput } from "@/components/auth/CodeInput";
import { Button } from "@/components/ui/Button";
import { FormMessage, Splash } from "@/components/ui/Feedback";
import { Field, Form } from "@/components/ui/Field";
import { IconTile } from "@/components/ui/IconTile";
import { useAuth } from "@/lib/auth-context";
import { describeAuthError } from "@/lib/auth-errors";

/**
 * The gate on everything under /app.
 *
 * Seven states, and the four middle ones are what this component exists for.
 * **None of them is "signed out", and collapsing any into it produces an
 * infinite loop** - the user signs in successfully and gets bounced straight
 * back to the form, forever, because the session was valid all along.
 *
 * `onboarding` - a self sign-up with a valid session and no workspace yet. The
 * most ordinary state in the product, and one form away from resolved. It goes
 * to `/onboarding`, never to the dead ends below.
 *
 * `pending` - a profile that exists but has been deactivated, or belongs to a
 * suspended company. Nothing the user can do about it themselves, so this gets
 * the "ask your administrator" screen.
 *
 * `error` - `/auth/me` failed for a reason that says nothing about the account:
 * the API was unreachable, or returned something unexpected. This is the state
 * a backend outage or a bad dev-server port lands in, and it must not read like
 * `pending` - a technician mid-outage does not need to be told to find an
 * administrator for a problem their administrator cannot fix either.
 *
 * `mfa` - the password was right and a second factor is outstanding. It is
 * handled **here** rather than at `/login`, because a session at `aal1` survives
 * a reload: without this, refreshing the page mid-challenge would leave a signed
 * -in user staring at a console that 403s every request.
 */
export function RequireAuth() {
  const { status, pendingReason, error, refresh, signOut, user } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <Splash label="Loading your account" />;
  }

  if (status === "signed-out") {
    // Remember where they were headed so sign-in can return them there rather
    // than dumping everyone on the overview.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (status === "onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  if (status === "pending") {
    return (
      <AuthLayout
        title="Account unavailable"
        subtitle={pendingReason ?? "Your account is not currently active."}
      >
        <div className="auth-stack">
          <IconTile tone="caution" size="2xl">
            <Clock />
          </IconTile>
          <p className="prose">
            Your sign-in worked, but this account cannot access TaskIQ right now. Ask your
            administrator to reactivate it - you will not need to sign in again once they do.
          </p>
          <Button variant="secondary" size="lg" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (status === "mfa") {
    return <MfaChallenge />;
  }

  if (status === "error") {
    return (
      <AuthLayout
        title="Can't reach TaskIQ"
        subtitle={error ?? "Something went wrong loading your account."}
      >
        <div className="auth-stack">
          <IconTile tone="caution" size="2xl">
            <WifiOff />
          </IconTile>
          <p className="prose">
            Your sign-in worked, but we could not load your account just now. This is usually
            temporary - try again in a moment.
          </p>
          <Button variant="primary" size="lg" onClick={() => void refresh()}>
            Try again
          </Button>
          <Button variant="secondary" size="lg" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <>
      <Outlet />
      <span className="sr-only">Signed in as {user?.email}</span>
    </>
  );
}

/**
 * The second factor, on the auth layout rather than inside the console.
 *
 * Nothing of the product is shown behind it: this session cannot read anything
 * yet, so a console skeleton with a dialog over it would be a frame full of
 * empty tables and failed requests.
 *
 * There is no "trust this device". It is a real feature and a real decision -
 * how long, revocable from where, what a stolen laptop means - and a checkbox
 * that quietly turns 2FA off for thirty days is not the place to make it.
 */
function MfaChallenge() {
  const { verifyMfa, signOut } = useAuth();
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyMfa(code);
      // No navigate: a verified factor issues a new session, the auth listener
      // fires, and `status` becomes `ready` underneath this component.
    } catch (err) {
      setError(describeAuthError(err));
      setCode("");
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Two-step verification"
      subtitle="Enter the six-digit code from your authenticator app."
      footer={
        <button type="button" className="auth-alt" onClick={() => void signOut()}>
          Sign in as somebody else
        </button>
      }
    >
      <Form onSubmit={onSubmit}>
        <Field label="Authentication code" htmlFor="mfa-code" required>
          <CodeInput
            id="mfa-code"
            value={code}
            onChange={setCode}
            invalid={Boolean(error)}
            autoFocus
          />
        </Field>

        <FormMessage>{error}</FormMessage>

        <Button
          type="submit"
          size="lg"
          block
          loading={submitting}
          disabled={code.length < TOTP_CODE_LENGTH}
          className="auth-submit"
        >
          Verify
        </Button>
      </Form>
    </AuthLayout>
  );
}

/**
 * The inverse, for /login and /signup: an already-signed-in visitor should not
 * be looking at a sign-in form.
 *
 * `onboarding` is sent to `/onboarding` rather than `/app` - landing there would
 * hit `RequireAuth`, which would redirect straight back here. Two guards that
 * each think the other should handle a state is how a redirect loop is built.
 *
 */
export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <Splash />;
  }

  if (status === "onboarding") return <Navigate to="/onboarding" replace />;

  // `mfa` is in this list, and it is the easy one to miss. That session is real
  // and its password step is done, so leaving it on the sign-in form would ask
  // for a password it has already accepted - the same loop the file above warns
  // about. `RequireAuth` shows it the code step instead.
  if (status === "ready" || status === "pending" || status === "error" || status === "mfa") {
    // Where `RequireAuth` sent them from, if they arrived here via a bounce
    // rather than by choice - see the `state` it attaches below.
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? "/app"} replace />;
  }

  return <>{children}</>;
}
